import {rememberPreviousPage} from '@/util/sourcePage';
import {trackHubspotPageView} from '@/util/hubspotTracking';
import '@/styles/globals.scss';
// Toast styles must be global (loaded in the initial CSS bundle), not tied to the
// lazily/visibility-mounted Toster chunk — otherwise a toast fired before that chunk
// loads renders unstyled and its status SVG icons expand to fill the viewport.
import '@/components/Toast/toast.css';
import {NextPage, NextComponentType, NextPageContext} from 'next';
import Script from 'next/script';
import type {AppProps} from 'next/app';
import dynamic from 'next/dynamic';
import {useState, useEffect, useMemo} from 'react';
import {CacheProvider, EmotionCache} from '@emotion/react';
import createEmotionCache from '@/createEmotionCache';
import {useCallback} from 'react';

import theme from '@/theme';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import WhatsAppFloat from '@/components/atoms/WhatsAppFloat';

const clientSideEmotionCache = createEmotionCache();

const Toster = dynamic(() => import('@/components/Toster'), {ssr: false, loading: () => null});
const HubspotScript = dynamic(() => import('@/components/Common/HubspotScript'), {ssr: false, loading: () => null});
const CookieConsent = dynamic(() => import('@/components/Common/CookieConsent'), {ssr: false, loading: () => null});
const ClarityScript = dynamic(() => import('@/components/Common/clarity-script'), {ssr: false, loading: () => null});
const SmoothScrollProvider = dynamic(() => import('@/components/Common/SmoothScrollProvider'), {ssr: false, loading: () => null});

import LazySection from '@/components/Common/LazySection';
import {ContactFormAwarePopupManager, ContactFormDialog, openContactForm} from '@/components/Common/ContactFormDialog';
import MainLayout from '@/layouts/main-layout';
type LayoutKeys = keyof typeof Layouts;

// Main is static: 180 pages use it, so it belongs in the shared bundle. The other five serve
// 1–5 pages each and load on demand (still server-rendered), so their code doesn't ship to
// every page.
const Layouts = {
    Main: MainLayout,
    Contact: dynamic(() => import('@/layouts/contactus-layout')),
    Blog: dynamic(() => import('@/layouts/blog-layout')),
    Career: dynamic(() => import('@/layouts/career-layout')),
    Blockchain: dynamic(() => import('@/layouts/Blockchain-layout')),
    News: dynamic(() => import('@/layouts/news-layout'))
};

export type MyPage<P = {}, IP = P> = NextPage<P, IP> & {
    Layout?: LayoutKeys;
};

export type MyAppProps = AppProps & {
    Component: NextComponentType<NextPageContext, any, any> & {
        Layout: LayoutKeys;
    };
    emotionCache?: EmotionCache;
};

// NOTE: the `next/font/local` loader that used to live here was removed. It emitted a
// second, hashed copy of public/fonts/Inter28pt-{Regular,Bold}.woff2 into
// _next/static/media/ and preloaded it — so every page downloaded both fonts TWICE
// (231 KB, 2 extra requests). Nothing consumed its --font-inter variable, and every
// text rule in globals.scss already sets `font-family: 'Headers'/'RegularText'
// !important` from the @font-face rules in styles/_variables.scss, so the loader's
// class had no effect on rendering. Fonts are now preloaded directly in _document.tsx.

const AppProviders = ({children, emotionCache}: {children: React.ReactNode; emotionCache: EmotionCache}) => {
    return (
        <CacheProvider value={emotionCache}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <SmoothScrollProvider />
                {children}
            </ThemeProvider>
        </CacheProvider>
    );
};

function App({Component, emotionCache = clientSideEmotionCache, pageProps, router}: MyAppProps) {
    const Layout: any = Layouts[Component.Layout];
    const [mountToaster, setMountToaster] = useState(false);
    const [mountHubspot, setMountHubspot] = useState(false);
    const [cookieReady, setCookieReady] = useState(false);
    const [whatsappReady, setWhatsappReady] = useState(false);
    // TODO: Set back to false once consent gating is approved by the client.
    // All consent logic (cookie check, onAccept callback, script gates) is fully
    // wired — flip this to false and redeploy to activate enforcement.
    const [consentGranted, setConsentGranted] = useState(true);

    // Restore consent state for returning visitors who already accepted.
    // Runs once on mount so tracking scripts load without waiting for the
    // banner interaction when consent was previously given.
    useEffect(() => {
        const match = document.cookie.split(';').find((c) => c.trim().startsWith('cookie-consent-accepted='));
        if (match?.trim().split('=')[1] === 'true') setConsentGranted(true);
    }, []);

    const handleGrantConsent = useCallback(() => setConsentGranted(true), []);

    // Remember the page being left on every client-side navigation, so lead forms can
    // send HubSpot the page the visitor came from (see util/sourcePage).
    useEffect(() => {
        const handleRouteChangeStart = () => rememberPreviousPage(globalThis.location.href);
        router.events.on('routeChangeStart', handleRouteChangeStart);
        return () => router.events.off('routeChangeStart', handleRouteChangeStart);
    }, [router.events]);

    // HubSpot only records the page its tracking code loaded on; report every later
    // client-side page so the lead's journey is complete (see util/hubspotTracking).
    useEffect(() => {
        router.events.on('routeChangeComplete', trackHubspotPageView);
        return () => router.events.off('routeChangeComplete', trackHubspotPageView);
    }, [router.events]);

    // Staggered deferred mounts — each component gets its own idle slot so
    // they don't all block the main thread in a single 370ms task.
    // Tier 1 (≤2s): cookie banner + WhatsApp icon — lightweight, visible UI.
    // Tier 2 (≤3s): Toaster + Clarity — small, non-blocking after mount.
    // Tier 3 (≤5s): HubSpot — heaviest script, loaded last.
    useEffect(() => {
        let fastId: ReturnType<typeof requestIdleCallback> | undefined;
        let fastTimer: ReturnType<typeof setTimeout> | undefined;
        let toasterSlowId: ReturnType<typeof requestIdleCallback> | undefined;
        let toasterSlowTimer: ReturnType<typeof setTimeout> | undefined;
        let hubspotSlowId: ReturnType<typeof requestIdleCallback> | undefined;
        let hubspotSlowTimer: ReturnType<typeof setTimeout> | undefined;

        const scheduleFast = () => {
            setCookieReady(true);
            setWhatsappReady(true);
        };

        if (typeof requestIdleCallback !== 'undefined') {
            fastId = requestIdleCallback(scheduleFast, {timeout: 2000});
            toasterSlowId = requestIdleCallback(() => setMountToaster(true), {timeout: 3000});
            hubspotSlowId = requestIdleCallback(() => setMountHubspot(true), {timeout: 5000});
        } else {
            fastTimer = setTimeout(scheduleFast, 1500);
            toasterSlowTimer = setTimeout(() => setMountToaster(true), 2000);
            hubspotSlowTimer = setTimeout(() => setMountHubspot(true), 3000);
        }

        return () => {
            if (fastId !== undefined) cancelIdleCallback(fastId);
            if (fastTimer !== undefined) clearTimeout(fastTimer);
            if (toasterSlowId !== undefined) cancelIdleCallback(toasterSlowId);
            if (toasterSlowTimer !== undefined) clearTimeout(toasterSlowTimer);
            if (hubspotSlowId !== undefined) cancelIdleCallback(hubspotSlowId);
            if (hubspotSlowTimer !== undefined) clearTimeout(hubspotSlowTimer);
        };
    }, []);

    // Dev-only: a stale next-pwa service worker (left over from a prior
    // production build) can keep serving an outdated CSS-in-JS bundle in
    // development, making fresh style changes appear "missing". Unregister any
    // existing SW and clear its caches in dev. Never runs in production.
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
        navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((reg) => reg.unregister()));
        if (typeof caches !== 'undefined') {
            caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
        }
    }, []);

    // The page element is memoized so this component's own state (the deferred cookie banner, WhatsApp button,
    // toaster and HubSpot mounts, consent) doesn't re-render the whole page a few seconds after load. Those
    // re-renders were wasted work, and they force React to client-render page sections it hydrates lazily
    // (CvSection) — rebuilding their HTML instead of hydrating it.
    const page = useMemo(() => <Component {...pageProps} openForm={openContactForm} />, [Component, pageProps]);

    // The contact form's open state lives in its own store (components/Common/ContactFormDialog), so
    // opening it re-renders only the dialog, not this whole tree. `openForm` stays a stable function.
    return Layout ? (
        <AppProviders emotionCache={emotionCache}>
            <div>
                <Layout openForm={openContactForm}>
                    {page}
                    <ContactFormDialog />

                    {whatsappReady && <WhatsAppFloat />}

                    {cookieReady && (
                        <LazySection>
                            <CookieConsent onAccept={handleGrantConsent} />
                        </LazySection>
                    )}
                    {mountToaster && (
                        <>
                            {/* Toast host must always mount once deferred — never gate it on
                                scroll visibility, or toasts can't render where the user is. */}
                            <Toster />
                            {consentGranted && <ClarityScript clarityId="gqe4ztskkr" />}
                        </>
                    )}
                    {mountHubspot && (
                        <>
                            {/* HubSpot chat widget is classified as functional (customer support
                                tool) — loaded without consent so the chat button is always
                                visible. HubSpot's analytics tracking is a disclosed side-effect
                                covered in the privacy policy. */}
                            <HubspotScript />
                        </>
                    )}
                    {/* All tracking scripts are gated behind explicit cookie consent.
                        Returning visitors who previously accepted load from the cookie
                        check above; new visitors load only after clicking Accept. */}
                    {consentGranted && (
                        <>
                            {/* Inline script only initialises dataLayer — tiny and static. */}
                            <Script id="gtm-init" strategy="lazyOnload">
                                {`window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});`}
                            </Script>
                            {/* GTM library loaded via src so the browser can cache it normally. */}
                            <Script
                                id="google-latest-script-tag"
                                strategy="lazyOnload"
                                src="https://www.googletagmanager.com/gtm.js?id=GTM-M2JTZB"
                            />
                            {process.env.NEXT_PUBLIC_ENABLE_AURADY === 'true' && (
                                <Script
                                    id="aurady-tracking"
                                    src="https://link.aurady.com/js/external-tracking.js"
                                    strategy="lazyOnload"
                                    data-tracking-id="tk_8fbe24d129154c97a30dab194ea3ae89"
                                />
                            )}
                        </>
                    )}
                </Layout>
                <ContactFormAwarePopupManager />
            </div>
        </AppProviders>
    ) : (
        <AppProviders emotionCache={emotionCache}>
            <Component {...pageProps} />
        </AppProviders>
    );
}

export default App;
